import { useRef, useState, useEffect } from "react";
import { Play, Pause, Heart, TrendUp, MusicNote, CaretDown, Sparkle, WhatsappLogo } from "@phosphor-icons/react";
import { optimizeImg } from "@/lib/img";
import { API } from "@/lib/api";

// Each Top-5 mock track points to a procedurally-generated, genre-styled
// demo loop served by the backend. They are placeholders for the social-proof
// preview; real audio comes from the Suno integration when ready.
const SAMPLE = (g) => `${API}/static/samples/${g}.mp3`;

const TOP_SONGS = [
  {
    rank: 1,
    title: "Reina del Pacífico",
    by: "Sofía R.",
    city: "Mazatlán, Sin.",
    genre: "Reggaeton",
    accent: "#FF4B00",
    plays: "12.4k",
    likes: "3.2k",
    cover: "https://images.pexels.com/photos/1267317/pexels-photo-1267317.jpeg",
    audio: SAMPLE("reggaeton"),
  },
  {
    rank: 2,
    title: "Amor de Tijuana",
    by: "Roberto V.",
    city: "Tijuana, B.C.",
    genre: "Balada Romántica",
    accent: "#FF4B00",
    plays: "9.8k",
    likes: "2.7k",
    cover: "https://images.unsplash.com/photo-1557241597-9e5b0a3af54c",
    audio: SAMPLE("balada_romantica"),
  },
  {
    rank: 3,
    title: "Salsa pa' mi Abuelo",
    by: "Miguel Á.",
    city: "Veracruz, Ver.",
    genre: "Salsa",
    accent: "#FF5A1E",
    plays: "8.1k",
    likes: "2.1k",
    cover: "https://images.unsplash.com/photo-1504609813442-a8924e83f76e",
    audio: SAMPLE("salsa"),
  },
  {
    rank: 4,
    title: "Cumbia de mi Barrio",
    by: "Doña Lupita R.",
    city: "Ciudad de México",
    genre: "Cumbia Colombiana",
    accent: "#4BC8DC",
    plays: "6.5k",
    likes: "1.8k",
    cover: "https://images.unsplash.com/photo-1514525253161-7a46d19cd819",
    audio: SAMPLE("cumbia"),
  },
  {
    rank: 5,
    title: "Bar La Cabrona",
    by: "Doña Irma",
    city: "Col. El Mante, Zapopan",
    genre: "Banda",
    accent: "#FFB800",
    plays: "5.9k",
    likes: "1.5k",
    cover: "https://images.pexels.com/photos/2796105/pexels-photo-2796105.jpeg",
    audio: SAMPLE("banda"),
  },
];

export default function TopSongs() {
  const audioRef = useRef(null);
  const [playingRank, setPlayingRank] = useState(null);
  const [openLyricsRank, setOpenLyricsRank] = useState(null);
  const [demoByRank, setDemoByRank] = useState({});

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onEnded = () => setPlayingRank(null);
    a.addEventListener("ended", onEnded);
    return () => a.removeEventListener("ended", onEnded);
  }, []);

  // Lyrics + real Suno audio for each demo track (cached server-side).
  useEffect(() => {
    fetch(`${API}/public/top-songs`)
      .then((r) => (r.ok ? r.json() : { songs: [] }))
      .then((data) => {
        const map = {};
        (data.songs || []).forEach((s) => { map[s.rank] = s; });
        setDemoByRank(map);
      })
      .catch(() => {});
  }, []);

  const toggle = (song) => {
    const a = audioRef.current;
    if (!a) return;
    if (playingRank === song.rank) {
      a.pause();
      setPlayingRank(null);
      return;
    }
    const demo = demoByRank[song.rank];
    // Real Suno track (voice + music) when available; genre sample as fallback
    a.src = demo?.audio ? `${API}/static/samples/${demo.audio}` : song.audio;
    a.volume = 0.7;
    a.play().then(() => {
      setPlayingRank(song.rank);
      if (demoByRank[song.rank]?.lyrics) setOpenLyricsRank(song.rank);
    }).catch(() => setPlayingRank(null));
  };

  const toggleLyrics = (rank) => {
    setOpenLyricsRank((cur) => (cur === rank ? null : rank));
  };

  return (
    <section id="top-canciones" className="bg-[#0a0a0a] border-t border-white/5">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="flex items-start justify-between gap-4 mb-10 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFB800]/10 border border-[#FFB800]/20 mb-3">
              <TrendUp size={14} weight="bold" className="text-[#FFB800]" />
              <span className="text-xs uppercase tracking-widest text-[#FFB800] font-semibold">Lo más escuchado</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Top 5 canciones <span className="text-[#FFB800]">de la semana</span>
            </h2>
            <p className="text-sm text-white/50 mt-3 max-w-md">Hechas por mexicanos como tú. Toca play y escucha 12 segundos de muestra.</p>
          </div>
        </div>

        <div className="space-y-3" data-testid="top-songs-list">
          {TOP_SONGS.map((song) => {
            const isPlaying = playingRank === song.rank;
            const lyrics = demoByRank[song.rank]?.lyrics;
            const lyricsOpen = openLyricsRank === song.rank;
            return (
              <div
                key={song.rank}
                data-testid={`top-song-${song.rank}`}
                className={`rounded-2xl border transition-all ${isPlaying ? "bg-[#FFB800]/5 border-[#FFB800]/30" : "bg-[#121212] border-white/5 hover:border-white/15 hover:bg-[#161616]"}`}
              >
                <div className="group flex items-center gap-3 sm:gap-5 p-3 sm:p-4">
                <div className="font-display text-3xl sm:text-5xl font-black w-8 sm:w-14 text-center shrink-0" style={{ color: isPlaying ? "#FFB800" : "rgba(255,255,255,0.15)" }}>
                  {song.rank}
                </div>

                <div className="relative w-14 h-14 sm:w-20 sm:h-20 rounded-xl overflow-hidden shrink-0">
                  <img src={optimizeImg(song.cover, 200)} alt={song.title} loading="lazy" decoding="async" width="80" height="80" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/30"></div>
                  <button
                    onClick={() => toggle(song)}
                    aria-label={isPlaying ? `Pausar ${song.title}` : `Reproducir ${song.title}`}
                    data-testid={`top-song-play-${song.rank}`}
                    className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/60 transition-colors"
                  >
                    {isPlaying
                      ? <Pause size={22} weight="fill" className="text-[#FFB800]" />
                      : <Play size={22} weight="fill" className="text-white" />}
                  </button>
                </div>

                <div className="min-w-0 flex-1">
                  <p className="font-display font-bold text-base sm:text-lg text-white truncate">{song.title}</p>
                  <p className="text-xs sm:text-sm text-white/50 truncate">
                    Por <span className="text-white/80">{song.by}</span> · {song.city}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span translate="no" className="notranslate text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full" style={{ backgroundColor: song.accent, color: "#000" }}>
                      {song.genre}
                    </span>
                    {lyrics && (
                      <button
                        onClick={() => toggleLyrics(song.rank)}
                        data-testid={`top-song-lyrics-btn-${song.rank}`}
                        className={`inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full border transition-colors ${lyricsOpen ? "bg-[#FFB800] text-black border-[#FFB800]" : "bg-transparent text-[#FFB800] border-[#FFB800]/40 hover:border-[#FFB800]"}`}
                      >
                        <MusicNote size={11} weight="fill" />
                        {lyricsOpen ? "Ocultar letra" : "Ver letra"}
                        <CaretDown size={10} weight="bold" className={`transition-transform ${lyricsOpen ? "rotate-180" : ""}`} />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        const shareUrl = `${API}/share/demo/${song.rank}`;
                        const text = encodeURIComponent(`🎵 Escucha "${song.title}" (${song.genre}) creada con IA en Tu MusiKa:\n${shareUrl}`);
                        window.open(`https://wa.me/?text=${text}`, "_blank");
                      }}
                      aria-label={`Compartir ${song.title} por WhatsApp`}
                      className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-[#25D366]/15 text-[#25D366] hover:bg-[#25D366] hover:text-black border border-transparent transition-colors"
                      data-testid={`top-song-share-${song.rank}`}
                    >
                      <WhatsappLogo size={11} weight="fill" /> Compartir
                    </button>
                  </div>
                </div>

                <div className="hidden sm:flex flex-col items-end shrink-0 gap-1">
                  <div className="flex items-center gap-1.5 text-white/60 text-sm">
                    <Play size={12} weight="fill" /> {song.plays}
                  </div>
                  <div className="flex items-center gap-1.5 text-white/40 text-xs">
                    <Heart size={12} weight="fill" className="text-[#FF4B00]/80" /> {song.likes}
                  </div>
                </div>
                </div>

                {lyrics && lyricsOpen && (
                  <div data-testid={`top-song-lyrics-${song.rank}`} className="px-4 sm:px-6 pb-5 pt-1 border-t border-white/5">
                    <div className="flex items-center gap-2 mb-3 mt-3">
                      <Sparkle size={14} weight="fill" className="text-[#FFB800]" />
                      <span className="text-[10px] uppercase tracking-widest text-[#FFB800]/90 font-semibold">Letra compuesta con IA a partir de una historia real</span>
                    </div>
                    <pre className="whitespace-pre-wrap font-sans text-[13px] sm:text-sm leading-relaxed text-white/70 max-h-64 overflow-y-auto pr-2" style={{ scrollbarWidth: "thin" }}>
{lyrics}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-white/30 mt-8">
          * Posiciones referenciales. Canciones de muestra creadas 100% con nuestra IA — letra, voz y música — igual que la tuya.
        </p>

        <audio ref={audioRef} preload="none" data-testid="top-songs-audio" />
      </div>
    </section>
  );
}
